/* eslint-disable @typescript-eslint/no-explicit-any */
import { type FC, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { FieldErrorMessage, SimpleText } from "@/components/atoms";
import { TagsValue } from "@/components/ui/shadcn-io/tags";
import { cn } from "@/lib/utils";

type PhoneTagsInputProps = {
  name: string;
  label?: string;
  placeholder?: string;
  "data-testid"?: string;
  classes?: string;
};

const PhoneTagsInput: FC<PhoneTagsInputProps> = ({
  name,
  label,
  placeholder = "+99999999999, +90000000000",
  "data-testid": testId,
  classes,
}) => {
  const { setValue, formState } = useFormContext();
  const watchedPhones = useWatch({ name }) as string[] | undefined;

  const [inputValue, setInputValue] = useState("");
  const [phones, setPhones] = useState<string[]>([]);

  const error = formState.errors?.[name];
  const smsErrorMessage = Array.isArray(error)
    ? error.find((e: any) => e?.message)?.message
    : error?.message;

  useEffect(() => {
    if (Array.isArray(watchedPhones)) {
      setPhones(watchedPhones);
    }
  }, [watchedPhones]);

  const normalizeInput = (value: string) => {
    let v = value.replace(/[^0-9+\s()-]/g, "");

    if (v && v[0] !== "+") {
      v = `+${v.replace(/\+/g, "")}`;
    }

    return v;
  };

  const commitValue = () => {
    const trimmed = inputValue.trim().replace(/,$/, "");
    if (!trimmed) return;

    const updated = [...phones, trimmed];

    setPhones(updated);
    setValue(name, updated, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      commitValue();
    }
  };

  const handleRemove = (index: number) => {
    const updated = phones.filter((_, i) => i !== index);

    setPhones(updated);
    setValue(name, updated, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <div className={cn("flex flex-col gap-1", classes)}>
      {label && <label className="text-sm font-medium">{label}</label>}

      <SimpleText classes="text-xs">
        You can add multiple SMS phone numbers by separating them by commas.
      </SimpleText>

      <div className="flex flex-wrap items-center gap-2 border rounded-[6px] px-2 py-[5px] relative">
        {phones.map((phone, index) => (
          <TagsValue
            key={`${phone}-${index}`}
            className="bg-gray-100 text-sm text-gray-950"
            onRemove={() => handleRemove(index)}
            data-testid={`phone-tag-${phone}`}
          >
            {phone}
          </TagsValue>
        ))}

        <input
          type="text"
          value={inputValue}
          onChange={e => setInputValue(normalizeInput(e.target.value))}
          onKeyDown={handleKeyDown}
          onBlur={commitValue}
          placeholder={phones.length === 0 ? placeholder : ""}
          className="flex-grow outline-none text-sm py-1"
          data-testid={testId}
        />

        <FieldErrorMessage message={smsErrorMessage as string} />
      </div>
    </div>
  );
};

export default PhoneTagsInput;
